import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.#cartTrigger?.addEventListener('click', this.#handleCartTriggerClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.#cartTrigger?.removeEventListener('click', this.#handleCartTriggerClick);
  }

  #handleCartAdd = () => {
    if (!this.hasAttribute('auto-open')) return;

    // When the drawer is hidden (e.g. landing pages that hide the header group),
    // opening the modal would lock body scroll on an invisible dialog and freeze
    // the page. Fall back to the cart page instead.
    if (!this.#canDisplay()) {
      this.#goToCart();
      return;
    }

    this.showDialog();
  };

  get #cartTrigger() {
    return this.querySelector('[data-testid="cart-drawer-trigger"]');
  }

  #handleCartTriggerClick = (event) => {
    event.preventDefault();
    this.open();
  };

  /**
   * Whether the drawer is actually rendered and can be shown as a modal.
   * Returns false when the element (or an ancestor) is `display: none`.
   *
   * @returns {boolean}
   */
  #canDisplay() {
    if (typeof this.checkVisibility === 'function') {
      return this.checkVisibility({ checkVisibilityCSS: true });
    }
    return this.getClientRects().length > 0;
  }

  #goToCart() {
    const cartUrl = window.Theme?.routes?.cart_url || '/cart';
    window.location.assign(cartUrl);
  }

  open() {
    // Guard against locking scroll on an invisible dialog (see #handleCartAdd).
    if (!this.#canDisplay()) {
      this.#goToCart();
      return;
    }

    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
